import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface VendorData {
  vendor: string;
  amount: number;
  count: number;
  change?: number;
}

interface VendorSpendingChartProps {
  data: VendorData[];
  title?: string;
}

const COLORS = ['#06b6d4', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

export function VendorSpendingChart({ data, title = 'Top Vendors by Spending' }: VendorSpendingChartProps) {
  const sortedData = [...data].sort((a, b) => b.amount - a.amount).slice(0, 7);
  const totalSpend = sortedData.reduce((sum, v) => sum + v.amount, 0);

  if (data.length === 0) {
    return (
      <Card className="p-6 border-border/50">
        <h3 className="text-base md:text-lg font-bold text-foreground mb-4">{title}</h3>
        <div className="h-[300px] flex items-center justify-center text-muted-foreground">
          No vendor data available
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-4 md:p-6 border-border/50">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base md:text-lg font-bold text-foreground">{title}</h3>
        <Badge variant="secondary" className="rounded-xl">
          {data.length} vendors
        </Badge>
      </div>

      <div className="h-[250px] md:h-[300px] mb-4">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sortedData} layout="vertical" margin={{ left: 0, right: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" horizontal={false} />
            <XAxis 
              type="number" 
              stroke="hsl(var(--muted-foreground))" 
              tick={{ fontSize: 11 }}
              tickFormatter={(value) => `₹${(value / 1000).toFixed(0)}K`}
            />
            <YAxis 
              type="category" 
              dataKey="vendor" 
              stroke="hsl(var(--muted-foreground))" 
              tick={{ fontSize: 11 }}
              width={100}
              tickFormatter={(value) => value.length > 12 ? value.slice(0, 12) + '...' : value}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--card))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                fontSize: '12px',
              }}
              formatter={(value: number) => [formatCurrency(value), 'Amount']}
            />
            <Bar dataKey="amount" radius={[0, 4, 4, 0]}>
              {sortedData.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Vendor List with Details */}
      <div className="space-y-2 max-h-[200px] overflow-y-auto">
        {sortedData.map((vendor, idx) => {
          const percentage = ((vendor.amount / totalSpend) * 100).toFixed(1);
          return (
            <div key={vendor.vendor} className="flex items-center justify-between p-2 rounded-lg hover:bg-surface transition-colors">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full" 
                  style={{ backgroundColor: COLORS[idx % COLORS.length] }} 
                />
                <div>
                  <p className="text-sm font-medium text-foreground truncate max-w-[120px] sm:max-w-none">
                    {vendor.vendor}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {vendor.count} invoice{vendor.count !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-medium text-foreground">{formatCurrency(vendor.amount)}</p>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-xs text-muted-foreground">{percentage}%</span>
                  {vendor.change !== undefined && (
                    vendor.change > 0 ? (
                      <TrendingUp className="h-3 w-3 text-success" />
                    ) : vendor.change < 0 ? (
                      <TrendingDown className="h-3 w-3 text-destructive" />
                    ) : (
                      <Minus className="h-3 w-3 text-muted-foreground" />
                    )
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
